import { FC } from 'react'
import { CanalEnvio, PreferenciasEnvio, canaisMarcados } from '../../core/montagem/envio'
import { EstadoCanal } from '../../hooks/useEnvioPaciente'
import { IconPaths } from '_metronic/helpers'
import styles from '../../Prescricao.module.scss'
import { Marca, MarcaEstado } from '../comum/MarcaEstado'
import { useRotulos } from '../comum/useRotulos'
import { Button } from 'app/components'

type IProps = {
  preferencias?: PreferenciasEnvio
  estados: Partial<Record<CanalEnvio, EstadoCanal>>
  onReenviar: (canal: CanalEnvio) => void
}

const ESTILO: Record<EstadoCanal, { marca: Marca; classe: string }> = {
  enviando: { marca: 'andamento', classe: styles.estadoAndamento },
  enviado: { marca: IconPaths.Check, classe: styles.estadoOk },
  falhou: { marca: IconPaths.Alert, classe: styles.estadoErro },
}

/**
 * Situação do envio em cada canal. "Enviado" quer dizer que o pedido foi aceito: o backend não
 * informa a entrega. O paciente abre com o código de acesso e os 4 primeiros dígitos do CPF.
 */
export const StatusEnvio: FC<IProps> = ({ preferencias, estados, onReenviar }) => {
  const { t } = useRotulos()
  const canais = preferencias ? canaisMarcados(preferencias) : []

  const destino = (canal: CanalEnvio) =>
    canal === 'email' ? preferencias?.enderecoEmail : preferencias?.telefone

  return (
    <section className='card card-bordered' aria-labelledby='resultado-envio'>
      <div className='card-header border-0 pt-5'>
        <h3 id='resultado-envio' className='card-title fs-5 fw-bold'>
          {t('prescricao.envio.titulo')}
        </h3>
      </div>
      <div className='card-body pt-0'>
        {canais.length === 0 ? (
          <p className={`m-0 ${styles.textoSecundario}`}>{t('prescricao.envio.nenhum_canal')}</p>
        ) : (
          <ul className='list-unstyled m-0' aria-live='polite'>
            {canais.map((canal, indice) => {
              const estado = estados[canal]
              const estilo = estado ? ESTILO[estado] : undefined
              return (
                <li
                  key={canal}
                  className={`d-flex flex-column gap-1 py-3 ${indice ? 'border-top' : ''}`}
                >
                  <div className='d-flex justify-content-between align-items-center gap-2'>
                    <span className='fw-bold'>{t(`prescricao.envio.${canal}`)}</span>
                    <span
                      className={`fw-bold fs-7 ${styles.comIcone} ${
                        estilo?.classe ?? styles.estadoNeutro
                      }`}
                    >
                      <MarcaEstado marca={estilo?.marca ?? IconPaths.Relogio} />
                      {t(`prescricao.envio.estado.${estado ?? 'pendente'}`)}
                    </span>
                  </div>
                  <span className={`fs-7 ${styles.textoSecundario}`}>{destino(canal)}</span>
                  {estado === 'falhou' && (
                    <span className={`fs-8 ${styles.estadoErro}`}>
                      {t('prescricao.envio.falhou_dica')}
                    </span>
                  )}
                  {estado !== 'enviando' && (
                    <Button
                      size='btn-sm'
                      className='btn-primary align-self-start mt-1'
                      onClick={() => onReenviar(canal)}
                      ariaLabel={t('prescricao.envio.reenviar_aria', {
                        canal: t(`prescricao.envio.${canal}`),
                      })}
                    >
                      {t('prescricao.envio.reenviar')}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        <p className={`fs-8 m-0 mt-3 pt-3 border-top ${styles.textoSecundario}`}>
          {t('prescricao.envio.como_abrir')}
        </p>
      </div>
    </section>
  )
}
